import { useId, useMemo } from 'react'
import { figure, passiveNodes, spreadLabels, type FigureView, type Rig } from '@/domain/rig'

/**
 * Das Strichmännchen — der Achsbaum, sichtbar und anklickbar.
 *
 * ES IST DIE BEDIENOBERFLÄCHE DES BAUMS UND NICHT SEINE ILLUSTRATION. Jeder
 * Kreis ist ein Knickpunkt, jeder Strich ein Glied, jeder Sektor eine
 * Winkelfreiheit. Ein Klick wählt einen DOF. Ein Bild, das nur zeigt, wäre eine
 * zweite Beschreibung neben dem Baum — dieses hier IST der Baum.
 *
 * DIE PROJEKTION LIEGT IM RECHENKERN, nicht hier. Dass die Seitenansicht x und z
 * trägt und nicht y, ist eine Aussage über die Maschine und hat dort Tests. Was
 * diese Datei tut, ist ausschliesslich Darstellung: Massstab, Farbe, Beschriftung.
 *
 * SVG UND NICHT CANVAS, aus denselben Gründen wie beim Diagramm: es bleibt bei
 * jeder Auflösung scharf, der Text darin wird vom Bildschirmleser gefunden, und
 * ein Klick trifft ein Element statt einer Koordinate, die man erst zurückrechnen
 * müsste.
 *
 * DIE BILDACHSE WIRD HIER UMGEDREHT. Der Rechenkern zählt `v` nach oben, ein
 * Bildschirm zählt nach unten. Die Umkehr gehört an genau diese Stelle — täte
 * sie der Kern, müsste man beim Lesen einer Projektion daran denken, dass sie
 * schon gedreht ist.
 */

export interface StickFigureProps {
  rig: Rig
  /** Der gewählte Knoten. */
  selected?: string | null
  onSelect?: (id: string) => void
  /** Doppelklick — die Gelenkdetailansicht. */
  onOpen?: (id: string) => void
  /** Kreissektoren für die Winkelfreiheiten. */
  freedoms?: boolean
  /** Die linke Körperseite mitzeichnen. */
  bothSides?: boolean
  /** Beschriftungen an den Punkten. */
  labels?: boolean
  /** Zwei mal zwei statt vier nebeneinander — jede Ansicht doppelt so gross. */
  large?: boolean
}

/** Bildgrösse je Ansicht, vor dem Massstab. */
const CELL = { w: 250, h: 430 }
const PAD = { top: 62, bottom: 34, side: 20 }

export function StickFigure({
  rig,
  selected = null,
  onSelect,
  onOpen,
  freedoms = false,
  bothSides = true,
  labels = true,
  large = false,
}: StickFigureProps) {
  const id = useId()
  /* Welche Gelenke passiv sind, kommt aus Ebene 2 — der Baum weiss es nicht. */
  const passive = useMemo(() => passiveNodes(rig), [rig])
  const views = useMemo(
    () => figure(rig, { bothSides, freedoms, passive }),
    [rig, bothSides, freedoms, passive],
  )

  const cols = large ? 2 : 4
  const rows = large ? 2 : 1
  const width = CELL.w * cols
  const height = CELL.h * rows

  return (
    <svg
      className={[
        'stick',
        large ? 'stick--large' : '',
        /*
          OB DAS BILD ÜBERHAUPT KLICKS ANNIMMT, ENTSCHEIDET EINE KLASSE UND
          NICHT EIN INLINE-STIL.

          Vorher stand auf jedem Punkt `pointerEvents: 'auto'`. Ein Inline-Stil
          schlägt jede Klassenregel — damit war auch die Regel ausgehebelt, die
          die blassen Punkte der GEGENSEITE vom Klicken ausnimmt. Sie liegen
          über den vorderen, und wer einen vorderen Punkt anklickte, traf sein
          blasses Spiegelbild: der Klick ging ins Leere.
        */
        onSelect ? '' : 'stick--static',
      ]
        .filter(Boolean)
        .join(' ')}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Gelenkpunkte in vier Ansichten, ${rig.nodes.length} Knickpunkte`}
    >
      {views.map((v, i) => (
        <Cell
          key={v.view.key}
          uid={`${id}-${v.view.key}`}
          view={v}
          x={(i % cols) * CELL.w}
          y={Math.floor(i / cols) * CELL.h}
          selected={selected}
          onSelect={onSelect}
          onOpen={onOpen}
          labels={labels}
          large={large}
        />
      ))}
    </svg>
  )
}

function Cell({
  uid,
  view,
  x,
  y,
  selected,
  onSelect,
  onOpen,
  labels,
  large,
}: {
  uid: string
  view: FigureView
  x: number
  y: number
  selected: string | null
  onSelect?: (id: string) => void
  onOpen?: (id: string) => void
  labels: boolean
  large: boolean
}) {
  const { bounds } = view
  const plotW = CELL.w - 2 * PAD.side
  const plotH = CELL.h - PAD.top - PAD.bottom

  /* Ein gemeinsamer Massstab für beide Bildachsen. Getrennte Massstäbe füllten
     das Feld besser aus und machten aus einem runden Fussabdruck ein Oval —
     und aus einem Winkel, den man abliest, eine Funktion des Seitenverhältnisses. */
  const spanU = Math.max(bounds.u1 - bounds.u0, 1)
  const spanV = Math.max(bounds.v1 - bounds.v0, 1)
  const k = Math.min(plotW / spanU, plotH / spanV)

  const cx = x + CELL.w / 2
  const cy = y + PAD.top + plotH / 2
  const X = (u: number) => cx + (u - (bounds.u0 + bounds.u1) / 2) * k
  /* Nach oben: der Kern zählt v aufwärts, der Bildschirm abwärts. */
  const Y = (v: number) => cy - (v - (bounds.v0 + bounds.v1) / 2) * k

  /*
    WAS „GROSS" VERGRÖSSERT — und was ausdrücklich nicht.

    Die Umschaltung legt zwei mal zwei Ansichten statt vier nebeneinander. Bei
    gleicher Bildbreite ist damit jede Ansicht doppelt so gross auf dem Schirm:
    dieselbe Zeichenfläche von 250 Einheiten füllt die doppelte Pixelbreite.

    GENAU DAS IST DER ZWECK — die Punkte sollen weiter auseinanderrücken. Das
    Strichmännchen wird also allein durch die Umschaltung grösser, ohne dass
    hier etwas skaliert werden müsste.

    Was NICHT mitwachsen soll, sind Punkte, Linienstärken und Schrift. Sie
    bekommen deshalb den Kehrwert der Vergrösserung: 0,5 Einheiten sind auf dem
    Schirm wieder ein Pixel. Vorher stand hier 1,6 — die Punkte wurden damit
    dreifach so gross wie normal und rückten optisch ENGER zusammen, obwohl das
    Skelett auseinanderging.
  */
  const scale = large ? 0.5 : 1

  /* Beschriftungen nur auf der rechten Seite und nur, wo sie lesbar bleiben:
     vier Ansichten nebeneinander haben je 250 Pixel Breite, und darin steht
     kein zweiter Satz Namen. */
  const marks = labels
    ? spreadLabels(
        view.dots
          .filter((d) => d.side === 1)
          .map((d) => ({ id: d.id, label: SHORT[d.id] ?? d.id, u: X(d.u), v: Y(d.v) })),
        11 * scale,
      )
    : []

  return (
    <g>
      {/* Trennlinie zur vorigen Ansicht. */}
      {x > 0 && (
        <line className="stick__sep" x1={x} y1={y + 14} x2={x} y2={y + CELL.h - 10} />
      )}

      <text className="stick__view" x={cx} y={y + 26} textAnchor="middle">
        {view.view.label}
      </text>
      {view.view.hint && (
        <text className="stick__hint" x={cx} y={y + 40} textAnchor="middle">
          {view.view.hint}
        </text>
      )}

      {/* Fadenkreuz am Nullpunkt — dem Kreuzgelenk des Rumpfaktuators. */}
      <g className="stick__origin">
        <line x1={X(0) - 13 * scale} y1={Y(0)} x2={X(0) + 13 * scale} y2={Y(0)} />
        <line x1={X(0)} y1={Y(0) - 13 * scale} x2={X(0)} y2={Y(0) + 13 * scale} />
      </g>

      {view.links.map((l, i) => (
        <line
          key={`${uid}-l-${i}`}
          className={`stick__link${l.side === -1 ? ' stick__link--far' : ''}`}
          x1={X(l.u1)}
          y1={Y(l.v1)}
          x2={X(l.u2)}
          y2={Y(l.v2)}
          strokeWidth={2.2 * scale}
        />
      ))}

      {view.arcs.map((a, i) => (
        <path
          key={`${uid}-a-${i}`}
          className="stick__arc"
          d={sector(X(a.u), Y(a.v), 15 * scale, a.lo, a.hi)}
        />
      ))}

      {view.dots.map((d, i) => {
        const on = d.id === selected
        const r = (d.id === 'cross' ? 5.5 : d.kind === 'gelenk' ? 3.8 : 3) * scale
        return (
          <circle
            key={`${uid}-d-${i}`}
            className={[
              'stick__dot',
              d.side === -1 ? 'stick__dot--far' : '',
              d.kind === 'anschluss' ? 'stick__dot--joint' : '',
              d.kind === 'mechanik' ? 'stick__dot--mech' : '',
              d.off ? 'stick__dot--off' : '',
              /* Passiv: Freiheit ohne Motor. Ein eigener Ring statt einer
                 eigenen Farbe — die Zeichnung bleibt monochrom, und der
                 Unterschied liegt in der Form. */
              d.passive ? 'stick__dot--passive' : '',
              on ? 'stick__dot--on' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            cx={X(d.u)}
            cy={Y(d.v)}
            r={on ? r * 1.5 : r}
            /* Der gewählte Punkt trägt einen breiten, blassen Ring: ein Punkt
               allein verschwindet zwischen den Nachbarn, ein Ring nicht. */
            strokeWidth={(on ? 4.5 : 1.7) * scale}
            onClick={() => onSelect?.(d.id)}
            onDoubleClick={() => onOpen?.(d.id)}
          >
            <title>{d.label}{d.passive ? ' · passiv, federgeführt' : ''}</title>
          </circle>
        )
      })}

      {marks.map((m) => (
        <text
          key={`${uid}-t-${m.id}`}
          className={m.id === selected ? 'stick__label stick__label--on' : 'stick__label'}
          x={m.u + 7 * scale}
          y={m.v + 3}
          fontSize={8 * scale}
        >
          {m.label}
        </text>
      ))}
    </g>
  )
}

/**
 * Ein Kreissektor von `lo` bis `hi`, in Grad gegen die Grundstellung.
 *
 * Null zeigt nach oben — so wie die Grundstellung des Roboters. Ein Sektor, der
 * bei drei Uhr anfängt, wäre mathematisch üblich und beim Ablesen einer
 * Kniebeugung nutzlos.
 */
function sector(cx: number, cy: number, r: number, lo: number, hi: number): string {
  const at = (deg: number) => {
    const a = ((-90 + deg) * Math.PI) / 180
    return { x: cx + r * Math.sin(a), y: cy + r * Math.cos(a) }
  }
  const p0 = at(lo)
  const p1 = at(hi)
  const big = Math.abs(hi - lo) > 180 ? 1 : 0
  return `M${cx.toFixed(1)} ${cy.toFixed(1)}L${p0.x.toFixed(1)} ${p0.y.toFixed(1)}A${r} ${r} 0 ${big} 1 ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}Z`
}

/**
 * Kurznamen für die Beschriftung.
 *
 * Die vollen Namen stehen im Baum und in der Sprechblase; im Bild ist „Hüft-
 * Nicken (B1)" bei 250 Pixel Feldbreite eine Zeile, die drei Punkte überdeckt.
 * Die Kürzel sind die Gelenkkennungen des Gen5-Stands.
 */
const SHORT: Record<string, string> = {
  cross: 'T2/T3',
  waistD: 'Bauchmotor',
  waistC: 'Bauchstange',
  chest: 'Oberkörper',
  pelvis: 'T1',
  hipG: 'B3 Gier',
  hipR: 'B2 Roll',
  hipN: 'B1 Nick',
  knee: 'B5 Knie',
  ankle: 'B7/B8',
  sole: 'Sohle',
  mtp: 'Vorfuss',
  shoulder: 'A1/A2',
  elbow: 'A4',
  wrist: 'Hand',
  headC: 'Kopf',
}
